<template>
  <div class="bitts-filter-tag-input">
    <div
      ref="triggerRef"
      role="textbox"
      :aria-disabled="disabled"
      :aria-label="ariaLabel"
      :tabindex="disabled ? -1 : 0"
      class="bitts-filter-tag-input__trigger"
      :class="{
        disabled,
        focused: isFocused,
      }"
      @click="focusInput"
    >
      <div class="bitts-filter-tag-input__content">
        <BittsTag
          v-for="(tag, index) in model"
          :key="index"
          size="x-small"
          color="neutral"
          class="bitts-filter-tag-input__tag"
        >
          <div class="flex items-center gap-4">
            <span class="bitts-filter-tag-input__tag-text">{{ tag }}</span>
            <button
              type="button"
              class="bitts-filter-tag-input__remove-button"
              :disabled="disabled"
              :aria-label="`Remove ${tag}`"
              @click.stop="removeTag(index)"
            >
              <FontAwesomeIcon
                :icon="['fak', 'x']"
                :style="{ width: '12px', height: '12px' }"
              />
            </button>
          </div>
        </BittsTag>

        <input
          ref="inputRef"
          v-model="inputValue"
          :id="id"
          type="text"
          class="bitts-filter-tag-input__input"
          :placeholder="model.length === 0 ? placeholder : ''"
          :disabled="disabled"
          @keydown="handleKeydown"
          @focus="isFocused = true"
          @blur="handleBlur"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, ref } from 'vue';

import BittsTag from '../BittsTag/BittsTag.vue';

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    disabled?: boolean;
    id?: string;
  }>(),
  {
    placeholder: 'Enter values',
    disabled: false,
    id: undefined,
  },
);

defineEmits<{
  change: [value: string[]];
}>();

const model = defineModel<string[]>({ default: () => [] });

const inputRef = ref<HTMLInputElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const inputValue = ref('');
const isFocused = ref(false);

const ariaLabel = computed(() => {
  const count = model.value.length;
  if (count === 0) return props.placeholder;
  return `${props.placeholder}, ${count} ${count === 1 ? 'value' : 'values'} entered`;
});

function focusInput() {
  if (!props.disabled) {
    inputRef.value?.focus();
  }
}

function addTag(value: string) {
  const trimmed = value.trim();
  if (trimmed && !model.value.includes(trimmed)) {
    model.value = [...model.value, trimmed];
  }
  inputValue.value = '';
}

function removeTag(index: number) {
  if (props.disabled) return;
  const newTags = [...model.value];
  newTags.splice(index, 1);
  model.value = newTags;
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    if (inputValue.value.trim()) {
      addTag(inputValue.value);
    }
  } else if (
    event.key === 'Backspace' &&
    !inputValue.value &&
    model.value.length > 0
  ) {
    removeTag(model.value.length - 1);
  }
}

function handleBlur() {
  isFocused.value = false;
  if (inputValue.value.trim()) {
    addTag(inputValue.value);
  }
}
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.bitts-filter-tag-input__trigger {
  @apply relative flex items-center gap-8 px-8 py-4 border border-neutral-border-strong rounded-lg
  bg-white cursor-text transition-colors shadow-component min-h-32;
}

.bitts-filter-tag-input__trigger.disabled {
  @apply opacity-60 pointer-events-none;
}

.bitts-filter-tag-input__trigger.focused {
  @apply border-neutral-border-strong;
}

.bitts-filter-tag-input__content {
  @apply flex-1 flex flex-wrap items-center gap-4 min-w-0;
}

.bitts-filter-tag-input__tag {
  @apply inline-flex items-center gap-4 shrink-0;
}

.bitts-filter-tag-input__tag-text {
  @apply max-w-[120px] truncate;
}

.bitts-filter-tag-input__remove-button {
  @apply text-neutral-text-weak hover:text-neutral-text cursor-pointer flex items-center;
}

.bitts-filter-tag-input__input {
  @apply flex-1 min-w-0 border-0 outline-none bg-transparent text-base text-neutral-text-strong;
}

.bitts-filter-tag-input__input::placeholder {
  @apply text-neutral-text-placeholder;
}

.bitts-filter-tag-input__input:disabled {
  @apply cursor-not-allowed;
}

:deep(.bitts-tag .ant-tag) {
  @apply m-0;
}
</style>
